// App-wide "robot may be stuck" alerts for the signed-in user's robots, on every
// page. Opened by ACTION_NAV_STUCK (a robot boxed in front and on both sides
// asking for help), closed by ACTION_NAV_UNSTUCK. A give-up keeps the alert up
// as "needs manual help" until the robot is freed. In-app only.
import React, { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { getSocket } from "../../services/socketService";
import { tokens } from "../../theme";
import BoxedInNotice from "../delivery/BoxedInNotice";
import { describeBoxedIn, fleetBoxedInReducer, initialFleetState } from "../../utils/boxedIn";

const serialOf = (data) => data?.serialID ?? data?.robotId ?? null;

// Spoken once per paging help request (`notify: true`); repeats within the
// robot's re-notify window arrive with `notify: false` and stay silent.
const announce = (text) => {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  const utterance = new window.SpeechSynthesisUtterance(text);
  utterance.volume = 0.5;
  window.speechSynthesis.speak(utterance);
};

const useOwnRobots = () => {
  const [robots, setRobots] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem("portalToken");
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/robots`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok && alive) setRobots(await res.json());
      } catch {
        /* silent — alerts stay off rather than breaking the layout */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  return robots;
};

const RobotStuckAlerts = () => {
  const navigate = useNavigate();
  const robots = useOwnRobots();
  const [state, dispatch] = useReducer(fleetBoxedInReducer, initialFleetState);
  const [now, setNow] = useState(() => Date.now());
  const robotsRef = useRef(new Map());
  robotsRef.current = new Map(robots.map((r) => [String(r.serialID), r]));

  const serialKey = robots
    .map((r) => r.serialID)
    .filter(Boolean)
    .join(",");

  useEffect(() => {
    if (!serialKey) return undefined;
    const serials = serialKey.split(",");
    const socket = getSocket();
    const join = () =>
      serials.forEach((serialID) => {
        socket.emit("subscribe-robot", serialID);
        socket.emit("frontend:get-ACTION_NAV_STUCK", { robotId: serialID });
      });

    const own = (data) => {
      const serial = serialOf(data);
      return serial != null && robotsRef.current.has(String(serial)) ? String(serial) : null;
    };
    const handler = (type) => (data) => {
      const serial = own(data);
      if (serial) dispatch({ type, serial, data, now: Date.now() });
    };
    const onNavProgress = handler("nav_progress");
    const onUnstuck = handler("unstuck");
    const onStuck = (data) => {
      const serial = own(data);
      if (!serial) return;
      dispatch({ type: "stuck", serial, data, now: Date.now() });
      if (data?.notify !== false) {
        const name = robotsRef.current.get(serial)?.name ?? "A robot";
        announce(`${name} may be stuck. Please clear the way around it.`);
      }
    };
    const onPulled = (reply) => {
      const serial = own(reply);
      const alert = reply?.alertACTION_NAV_STUCK;
      if (serial && alert) {
        // Cached, so of unknown age: shown, but never re-paged.
        dispatch({
          type: "stuck",
          serial,
          data: { ...alert, notify: false, pulled: true },
          now: Date.now(),
        });
      }
    };

    socket.on("connect", join);
    if (socket.connected) join();
    socket.on("nav_progress", onNavProgress);
    socket.on("ACTION_NAV_STUCK", onStuck);
    socket.on("ACTION_NAV_UNSTUCK", onUnstuck);
    socket.on("get_ACTION_NAV_STUCK", onPulled);
    // Rooms are left joined on cleanup: the shared socket's rooms are not
    // ref-counted, and the pages below use the same ones.
    return () => {
      socket.off("connect", join);
      socket.off("nav_progress", onNavProgress);
      socket.off("ACTION_NAV_STUCK", onStuck);
      socket.off("ACTION_NAV_UNSTUCK", onUnstuck);
      socket.off("get_ACTION_NAV_STUCK", onPulled);
    };
  }, [serialKey]);

  const visible = Object.keys(state.bySerial).filter((serial) => !state.dismissed[serial]);
  const hasAlerts = visible.length > 0;
  useEffect(() => {
    if (!hasAlerts) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [hasAlerts]);

  if (!hasAlerts) return null;

  return (
    <Stack
      gap={1}
      sx={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 1400,
        width: "min(380px, calc(100vw - 32px))",
      }}
    >
      {visible.map((serial) => {
        const view = describeBoxedIn(state.bySerial[serial], now);
        if (!view) return null;
        const robot = robotsRef.current.get(serial);
        return (
          <Box
            key={serial}
            sx={{ background: tokens.surface, borderRadius: "12px", boxShadow: tokens.shadowLg }}
          >
            <BoxedInNotice view={view}>
              <Stack direction="row" alignItems="center" gap={1} mt={1}>
                <Typography
                  sx={{ fontSize: 12, fontWeight: 700, color: tokens.text, flex: 1, minWidth: 0 }}
                  noWrap
                >
                  {robot?.name ?? serial}
                </Typography>
                {robot?._id && (
                  <Button size="small" onClick={() => navigate(`/robots/${robot._id}`)}>
                    View on map
                  </Button>
                )}
                <IconButton
                  size="small"
                  aria-label={`Dismiss alert for ${robot?.name ?? serial}`}
                  onClick={() => dispatch({ type: "dismiss", serial })}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Stack>
            </BoxedInNotice>
          </Box>
        );
      })}
    </Stack>
  );
};

export default RobotStuckAlerts;
